/**
 * Mobile navigation: a sticky top bar, and the rail as an off-canvas drawer.
 *
 * The rail used to sit in the document flow. At 375px that meant scrolling
 * past a screen of navigation to reach the chart, or — after the row order was
 * fixed — past the chart and the whole Details panel to reach navigation at
 * the very bottom. Neither is a navigation.
 *
 * So the two halves are split by how often they are used: instrument search is
 * the one thing wanted on every visit and stays in the top bar, always
 * reachable without opening anything; Recent and Screeners move behind the
 * menu button.
 *
 * The drawer is not rendered at all when closed. It therefore cannot consume
 * page height, cannot be tabbed into by accident, and cannot overflow the
 * viewport horizontally — three failure modes a translated-off-screen panel
 * has to be talked out of.
 */
import { useEffect, useRef } from "react";

import { InstrumentSearch } from "./InstrumentSearch.tsx";
import { Sidebar } from "./Sidebar.tsx";
import type { InstrumentOption } from "../domain/instruments.ts";
import type { ScreenerEntry } from "../fixtures/instruments.ts";

const DRAWER_ID = "mobile-nav-drawer";

interface MobileNavProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	activeCode: string;
	recents: readonly string[];
	activeScreener: string | null;
	onSelectInstrument: (code: string) => void;
	onSelectScreener: (slug: string | null) => void;
	catalogue?: readonly InstrumentOption[];
	catalogueOrigin?: "api" | "fixture";
	screeners?: readonly ScreenerEntry[];
	screenersOrigin?: "api" | "fixture";
}

export function MobileNav({
	open,
	onOpenChange,
	activeCode,
	recents,
	activeScreener,
	onSelectInstrument,
	onSelectScreener,
	catalogue,
	catalogueOrigin = "fixture",
	screeners,
	screenersOrigin = "fixture",
}: MobileNavProps) {
	const menuRef = useRef<HTMLButtonElement>(null);
	const wasOpen = useRef(open);

	// Escape closes from anywhere, and focus goes back to the control that
	// opened the drawer rather than to the top of the document.
	useEffect(() => {
		if (!open) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") onOpenChange(false);
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [open, onOpenChange]);

	useEffect(() => {
		if (wasOpen.current && !open) menuRef.current?.focus();
		wasOpen.current = open;
	}, [open]);

	return (
		<>
			<header className="topbar">
				<button
					ref={menuRef}
					type="button"
					className="topbar__menu"
					aria-label={open ? "Close navigation" : "Open navigation"}
					aria-expanded={open}
					aria-controls={DRAWER_ID}
					onClick={() => onOpenChange(!open)}
				>
					<span aria-hidden="true">☰</span>
				</button>

				<div className="topbar__brand">
					<span className="sidebar__mark" aria-hidden="true" />
					<span className="sidebar__wordmark">quantist</span>
				</div>

				<InstrumentSearch
					className="instrument-search--topbar"
					onSelect={onSelectInstrument}
					catalogue={catalogue}
					catalogueOrigin={catalogueOrigin}
					label="Search instrument"
					labelHidden
				/>
			</header>

			{open ? (
				<>
					{/* Tapping anywhere off the drawer closes it. Presentational: the
					    close button and Escape are the accessible paths. */}
					<div className="scrim" onClick={() => onOpenChange(false)} aria-hidden="true" />
					<Sidebar
						id={DRAWER_ID}
						variant="drawer"
						activeCode={activeCode}
						recents={recents}
						activeScreener={activeScreener}
						onSelectInstrument={onSelectInstrument}
						onSelectScreener={onSelectScreener}
						screeners={screeners}
						screenersOrigin={screenersOrigin}
						onClose={() => onOpenChange(false)}
					/>
				</>
			) : null}
		</>
	);
}
